import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiHeart, FiShoppingCart, FiStar, FiChevronLeft, FiChevronRight, FiX, FiZoomIn } from 'react-icons/fi';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import ProductCard from '../common/ProductCard';
import './ProductPage.css';

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, addToWishlist } = useApp();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);

  useEffect(() => {
    Promise.all([
      axios.get(`/api/products/${id}`),
      axios.get(`/api/reviews?productId=${id}&limit=10`),
      axios.get(`/api/products?similar=${id}&limit=4`),
    ]).then(([pRes, rRes, sRes]) => {
      setProduct(pRes.data.product);
      setReviews(rRes.data.reviews || []);
      setSimilar(sRes.data.products || []);
    }).catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Close the image zoom overlay with the Escape key.
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoomOpen]);

  if (loading) return <div className="page-container"><div className="product-loading">Loading…</div></div>;
  if (!product) return null;

  const media = [product.image1, product.image2, product.image3, product.image4, product.image5, product.video1, product.video2]
    .filter(Boolean);
  const videoExtensions = ['.mp4', '.webm', '.mov', '.ogg', '.avi', '.mkv', '.m4v'];
  const isVideo = (m) => {
    if (!m) return false;
    const lower = m.toLowerCase();
    return videoExtensions.some(ext => lower.endsWith(ext)) || lower.includes('video');
  };
  const videoType = (m) => {
    if (!m) return '';
    const lower = m.toLowerCase();
    if (lower.endsWith('.mp4') || lower.endsWith('.m4v')) return 'video/mp4';
    if (lower.endsWith('.webm')) return 'video/webm';
    if (lower.endsWith('.mov')) return 'video/quicktime';
    if (lower.endsWith('.ogg')) return 'video/ogg';
    return '';
  };
  const mediaSrc = (m) => m.startsWith('http') ? m : `/uploads/${m}`;

  const discountPct = product.origprice && product.selling_price
    ? Math.round(((product.origprice - product.selling_price) / product.origprice) * 100) : product.discount || 0;

  const userReviewCount = user ? reviews.filter(r => r.custid === user.custid).length : 0;
  const handleSubmitReview = async () => {
    if (!reviewText.trim()) return;
    if (!user) return;
    if (userReviewCount >= 3) return;
    try {
      await axios.post('/api/reviews', { productId: id, rating: reviewRating, description: reviewText });
      setReviewText(''); setReviewRating(5);
      const res = await axios.get(`/api/reviews?productId=${id}&limit=10`);
      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error(err);
      window.alert(err.response?.data?.message || 'Unable to submit review. Please try again.');
    }
  };

  return (
    <div className="page-container">
      <div className="product-page-nav">
        <button className="back-btn" onClick={() => navigate(-1)}><FiArrowLeft size={20}/></button>
        <span className="pp-logo" onClick={() => navigate('/')}>Abhiras</span>
      </div>

      <div className="pp-main">
        {/* Media */}
        <div className="pp-media">
          <div className="media-main">
            {media.length > 0 ? (
              isVideo(media[mediaIdx]) ? (
                <video key={media[mediaIdx]} src={mediaSrc(media[mediaIdx])}
                  type={videoType(media[mediaIdx])}
                  controls autoPlay muted className="media-vid" />
              ) : (
                <>
                  <img src={mediaSrc(media[mediaIdx])}
                    alt={product.product_name} className="media-img zoomable"
                    onClick={() => setZoomOpen(true)}
                    title="Click to zoom" />
                  <span className="media-zoom-hint" onClick={() => setZoomOpen(true)} title="Click to zoom">
                    <FiZoomIn size={16} />
                  </span>
                </>
              )
            ) : (
              <img src={`https://placehold.co/500x500/F5F0E8/800020?text=Abhiras`} alt="placeholder" className="media-img" />
            )}
            {media.length > 1 && <>
              <button className="media-prev" onClick={() => setMediaIdx(i => (i - 1 + media.length) % media.length)}><FiChevronLeft /></button>
              <button className="media-next" onClick={() => setMediaIdx(i => (i + 1) % media.length)}><FiChevronRight /></button>
            </>}
          </div>
          <div className="media-thumbs">
            {media.map((m, i) => (
              <button key={i} className={`thumb${i === mediaIdx ? ' active' : ''}`} onClick={() => setMediaIdx(i)}>
                {isVideo(m)
                  ? <span>▶</span>
                  : <img src={mediaSrc(m)} alt={`thumb ${i}`} />
                }
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="pp-info">
          <p className="pp-category">{product.category}</p>
          <h1 className="pp-name">{product.product_name}</h1>
          <div className="pp-price-row">
            <span className="pp-price">₹{Number(product.selling_price).toLocaleString('en-IN')}</span>
            {product.origprice > product.selling_price && <>
              <span className="pp-orig">₹{Number(product.origprice).toLocaleString('en-IN')}</span>
              <span className="pp-disc-badge">{discountPct}% OFF</span>
            </>}
          </div>
          {product.avg_rating > 0 && (
            <div className="pp-rating">
              {[1,2,3,4,5].map(s => <FiStar key={s} size={14} fill={s <= Math.round(product.avg_rating) ? '#C9A84C' : 'none'} color="#C9A84C" />)}
              <span>{Number(product.avg_rating).toFixed(1)} · {product.review_count} reviews</span>
            </div>
          )}
          <p className="pp-desc">{product.description}</p>
          {product.dimensions && <p className="pp-dims"><strong>Dimensions:</strong> {product.dimensions}</p>}
          {product.inventory !== undefined && product.inventory !== null && (
            <p className="pp-stock"><strong>Available Quantity:</strong> {Number(product.inventory)}</p>
          )}

          {/* Stock & Qty & Actions */}
          {product.inventory !== undefined && product.inventory !== null && Number(product.inventory) <= 0 ? (
            <div className="pp-out-of-stock">
              <p className="oos-text">Out of Stock</p>
              <button className="btn-outline pp-wish-btn" onClick={() => addToWishlist(product.product_id)}>
                <FiHeart size={16} /> Add to Wishlist
              </button>
            </div>
          ) : (
            <>
              <div className="pp-qty-row">
                <button className="qty-btn" onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                <span className="qty-val">{qty}</span>
                <button className="qty-btn" onClick={() => setQty(q => q + 1)}>+</button>
              </div>
              <div className="pp-actions">
                <button className="btn-primary pp-cart-btn" onClick={() => addToCart(product.product_id, qty)}>
                  <FiShoppingCart size={16} /> Add to Cart
                </button>
                <button className="btn-outline pp-wish-btn" onClick={() => addToWishlist(product.product_id)}>
                  <FiHeart size={16} /> Wishlist
                </button>
              </div>
              <button className="pp-buy-now" onClick={() => { addToCart(product.product_id, qty); navigate('/cart?tab=cart'); }}>
                Buy Now
              </button>
            </>
          )}
        </div>
      </div>

      {/* Reviews */}
      <div className="pp-section-head">Customer Reviews</div>
      <div className="reviews-section">
        {reviews.length === 0 && <p className="no-reviews">No reviews yet. Be the first!</p>}
        <div className={`reviews-list${reviews.length > 5 ? ' scrollable' : ''}`}>
          {reviews.map(r => (
            <div key={`${r.custid || 'anon'}-${r.writtenon}`} className="review-card">
              <div className="review-top">
                <div className="review-author-block">
                  <span className="review-avatar">{(r.author_name || r.username || 'C').charAt(0).toUpperCase()}</span>
                  <div>
                    <span className="review-author">{r.author_name || 'Customer'}</span>
                    {r.username && r.username !== r.author_name && <span className="review-handle">@{r.username}</span>}
                  </div>
                </div>
                <div className="review-stars">
                  {[1,2,3,4,5].map(s => <FiStar key={s} size={12} fill={s <= r.rating ? '#C9A84C' : 'none'} color="#C9A84C" />)}
                </div>
                <span className="review-date">{r.writtenon ? new Date(r.writtenon).toLocaleDateString('en-IN') : ''}</span>
              </div>
              <p className="review-text">{r.description}</p>
            </div>
          ))}
        </div>
        {!user && <p className="review-login-note">Please log in to submit a review.</p>}

        {/* Write review */}
        <div className="write-review">
          <h3>Write a Review</h3>
          {!user && <p className="review-login-note">Please sign in to leave a review.</p>}
          {user && userReviewCount >= 3 && (
            <p className="review-login-note">You have already posted 3 comments for this product.</p>
          )}
          <div className="star-select">
            {[1,2,3,4,5].map(s => (
              <button key={s} type="button" onClick={() => setReviewRating(s)} className={s <= reviewRating ? 'star-filled' : 'star-empty'}>
                <FiStar size={20} fill={s <= reviewRating ? '#C9A84C' : 'none'} color="#C9A84C" />
              </button>
            ))}
          </div>
          <textarea
            placeholder="Share your experience..."
            value={reviewText}
            onChange={e => setReviewText(e.target.value)}
            rows={3}
            disabled={!user || userReviewCount >= 3}
          />
          <button
            className="btn-primary"
            onClick={handleSubmitReview}
            disabled={!user || userReviewCount >= 3 || !reviewText.trim()}
          >
            Submit Review
          </button>
        </div>
      </div>

      {/* Similar products */}
      {similar.length > 0 && <>
        <div className="pp-section-head">Similar Products</div>
        <div className="similar-grid">
          {similar.map(p => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </>}

      {/* Image zoom lightbox */}
      {zoomOpen && media.length > 0 && !isVideo(media[mediaIdx]) && (
        <div className="pp-zoom-overlay" onClick={() => setZoomOpen(false)}>
          <button className="pp-zoom-close" onClick={() => setZoomOpen(false)} aria-label="Close zoom">
            <FiX size={26} />
          </button>
          {media.length > 1 && (
            <button className="pp-zoom-prev" aria-label="Previous"
              onClick={(e) => { e.stopPropagation(); setMediaIdx(i => (i - 1 + media.length) % media.length); }}>
              <FiChevronLeft size={30} />
            </button>
          )}
          <img src={mediaSrc(media[mediaIdx])} alt={product.product_name}
            className="pp-zoom-img" onClick={(e) => e.stopPropagation()} />
          {media.length > 1 && (
            <button className="pp-zoom-next" aria-label="Next"
              onClick={(e) => { e.stopPropagation(); setMediaIdx(i => (i + 1) % media.length); }}>
              <FiChevronRight size={30} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
